import { RefreshFlag } from "@/refresh_flag.tsx"
import { useLocation } from "wouter-preact"
import { useSearch } from "wouter-preact/use-location"
import { useFetch, usePageTitle } from "@/utils"
import { api_fetch, data_or_throw, DeductionDetailJson } from "@/api"
import { DetailNavbar } from "@/app/navbar.tsx"
import { CenterSpinner } from "@/app/loading.tsx"
import { DeductionDetailContent } from "@/deduction_detail/DeductionDetailContent.tsx"
import { DrawerDeduction } from "@/dispute/file_dispute_drawer"
import { loadOrgFieldConfigs } from "@/org_config/state.tsx"
import { should_show_detail_spinner } from "@/utils/detail_refresh.ts"
import { useEffect, useRef } from "preact/hooks"

interface DeductionDetailProps {
  params: { deduction_id: string }
}

export function DeductionDetail({ params }: DeductionDetailProps) {
  const deduction_id = params.deduction_id
  const statusFlag = RefreshFlag.use((sf: boolean) => sf!)
  let [, setLocation] = useLocation()
  const searchString = useSearch()
  const hasOpenedDispute = useRef(false)

  let {
    data: deduction,
    loading,
    setData: setDeduction,
  } = useFetch(async () => {
    let res = await api_fetch<DeductionDetailJson>(`/deduction/${deduction_id}`)
    return data_or_throw(res)
  }, [deduction_id, statusFlag])

  usePageTitle(deduction?.invoice_number ?? "Deduction")

  useEffect(() => {
    void loadOrgFieldConfigs()
  }, [])

  // Auto-open dispute drawer if query param is set
  useEffect(() => {
    if (!deduction || hasOpenedDispute.current) return

    const searchParams = new URLSearchParams(searchString)
    const shouldOpenDispute = searchParams.get("open_dispute") === "true"

    if (shouldOpenDispute) {
      hasOpenedDispute.current = true
      // Small delay to ensure the page is fully rendered
      setTimeout(() => {
        DrawerDeduction.set(deduction)
      }, 100)

      // Clear the query param from URL to prevent re-opening on refresh
      setLocation(`/deduction/${deduction_id}`, { replace: true })
    }
  }, [deduction, searchString, deduction_id, setLocation])

  // Only unmount the content for the initial load or a navigation to a different deduction.
  // Background refetches (RefreshFlag flips, e.g. after a status change) keep the content
  // mounted so unsaved edits in child components survive.
  if (!deduction || should_show_detail_spinner(loading, deduction.id, deduction_id)) {
    return (
      <div key={deduction_id} class="w-full flex flex-col">
        <DetailNavbar />
        <CenterSpinner />
      </div>
    )
  }

  return (
    <div key={deduction_id} class="w-full">
      <DetailNavbar />
      <DeductionDetailContent deduction={deduction} onUpdate={setDeduction} />
    </div>
  )
}
